"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var moment = require("moment");
var form_constants_1 = require("../form-definition/form-constants");
/**
 * All dates in the store are in yyyy-MM-dd.
 * However, some browsers (all of them except chrome and edge)
 * want to show dates in the format MM/dd/yyyy.
 * So, based on the browser we have detected,
 * we will return the properly formatted date.
 * @param {string} browserName The name of the browser.  The choices for what we support are 'chrome', 'firefox', 'ie', and 'edge'.
 * (This comes from detect-browser).
 * @param {string} dateString A date from the store, in string format, which will be yyyy-MM-dd.
 * @returns {string} A date string of the appropriate format yyyy-MM-dd or MM/dd/yyyy
 */
function getDateForBrowser(browserName, dateString) {
    var retval = dateString; // if we do nothing else, just return what we were passed in
    if (browserName !== 'chrome' && browserName !== 'edge') {
        retval = getUserFriendlyFormat(dateString);
    }
    return retval;
}
exports.getDateForBrowser = getDateForBrowser;
function getUserFriendlyFormat(dateString) {
    var retval = dateString;
    if (dateString) {
        var yyyyMMddFormatted = false;
        var yyyyMMddPattern = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}");
        yyyyMMddFormatted = yyyyMMddPattern.test(dateString);
        if (yyyyMMddFormatted) {
            retval = dateString.substring(5, 7) + "/" + dateString.substring(8) + "/" + dateString.substring(0, 4);
        }
    }
    return retval;
}
exports.getUserFriendlyFormat = getUserFriendlyFormat;
/**
 * Convert a JSON date string (can be either '2016-11-02T00:00:00-05:00' or '2017-09-28T10:09:20.9546978-05:00' or '2017-09-21T00:00:00') to a user friendly date (example: '11/02/2016')
 * If the incoming date string does not contain a timezone offset, we simply extract the date part and return.
 * If the incoming date string contains a timezone offset, moment.js is used to perform a local conversion.
 * @param {*} dateString A JSON date string (can be either '2016-11-02T00:00:00-05:00' or '2017-09-28T10:09:20.9546978-05:00' or '2017-09-21T00:00:00')
 * @returns A user friendly MM/dd/yyyy date (example: '11/02/2016') if the conversion was successful.
 * If the supplied dateString does not match any of the supported date formats, the originally supplied dateString is returned.
 */
function getUserFriendlyFormatFromJson(dateString) {
    var retval = dateString;
    if (dateString != null) {
        var timeZoneOffsetPresent = false; // set this to true if we found a non zero timezone offset.  The offset can be positive or negative.
        // Each pattern has to be tested separately because the timezone information may appear in different places.
        var jsonDatePattern1Matched = false; // Did we match pattern 1:  '2016-11-02T00:00:00-05:00'
        var jsonDatePattern2Matched = false; // Did we match pattern 2:  '2017-09-28T10:09:20.9546978-05:00'
        var jsonDatePattern3Matched = false; // Did we match pattern 3:  '2017-09-21T00:00:00'
        // pattern 1:  '2016-11-02T00:00:00-05:00'
        var jsonDatePattern1 = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}[T]\\d{2}[:]\\d{2}[:]\\d{2}[+-]\\d{2}[:]\\d{2}");
        jsonDatePattern1Matched = jsonDatePattern1.test(dateString);
        if (jsonDatePattern1Matched) {
            // get out the timezone offset, convert to numbers, and act accordingly
            var tzHrString = dateString.substring(20, 22);
            var tzMinString = dateString.substring(23, 25);
            var tzHrNum = parseInt(tzHrString, 10);
            var tzMinNum = parseInt(tzMinString, 10);
            if (tzHrNum > 0 || tzMinNum > 0) {
                timeZoneOffsetPresent = true;
            }
        }
        else {
            // pattern 2:  '2017-09-28T10:09:20.9546978-05:00'
            var jsonDatePattern2 = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}[T]\\d{2}[:]\\d{2}[:]\\d{2}[\\.]\\d{7}[+-]\\d{2}[:]\\d{2}");
            jsonDatePattern2Matched = jsonDatePattern2.test(dateString);
            if (jsonDatePattern2Matched) {
                // get out the timezone offset, convert to numbers, and act accordingly
                var tzHrString2 = dateString.substring(28, 30);
                var tzMinString2 = dateString.substring(31, 33);
                var tzHrNum2 = parseInt(tzHrString2, 10);
                var tzMinNum2 = parseInt(tzMinString2, 10);
                if (tzHrNum2 > 0 || tzMinNum2 > 0) {
                    timeZoneOffsetPresent = true;
                }
            }
            else {
                // pattern 3:  '2017-09-21T00:00:00'
                var jsonDatePattern3 = new RegExp("^\\d{4}[/-]\\d{2}[/-]\\d{2}[T]\\d{2}[:]\\d{2}[:]\\d{2}");
                jsonDatePattern3Matched = jsonDatePattern3.test(dateString);
                if (jsonDatePattern3Matched) {
                    timeZoneOffsetPresent = false;
                }
            }
        }
        // We can test for a match in the same statement since the date parts we care about are in the same place in any case.
        // If the place where the actual date parts are ever changes, just test for each pattern matched variable independently.
        if (jsonDatePattern1Matched || jsonDatePattern2Matched || jsonDatePattern3Matched) {
            if (timeZoneOffsetPresent) {
                // with a nonzero utc offset, we perform a local date conversion using moment.js.
                var dateMoment = moment(dateString);
                if (dateMoment != null) {
                    retval = dateMoment.format('MM/DD/YYYY');
                    if (retval != null) {
                        return retval;
                    }
                }
            }
            else {
                // with no UTC offset, we strip out just the date part and return it.
                retval = dateString.substring(5, 7) + "/" + dateString.substring(8, 10) + "/" + dateString.substring(0, 4);
                return retval;
            }
        }
    }
    return dateString;
}
exports.getUserFriendlyFormatFromJson = getUserFriendlyFormatFromJson;
// verifies that a date string of format yyyy-mm-dd or mm/dd/yyyy is valid
function isValidDate(dateString, country) {
    var yyyyMMddFormat = new RegExp("^\\d{4}[-](0?[1-9]|1[012])[-](0?[1-9]|[12][0-9]|3[01])");
    var ddMMyyyyFormat = new RegExp("^(0?[1-9]|[12][0-9]|3[01])[/](0?[1-9]|1[012])[/]\\d{4}");
    var mmDDyyyyFormat = new RegExp("^(0?[1-9]|1[012])[/](0?[1-9]|[12][0-9]|3[01])[/]\\d{4}");
    var day, month, year;
    if (mmDDyyyyFormat.test(dateString) && country === form_constants_1.CountryEnum.UnitedStates) {
        var date = dateString.split('/');
        month = parseInt(date[0], 10);
        day = parseInt(date[1], 10);
        year = parseInt(date[2], 10);
    }
    else if (yyyyMMddFormat.test(dateString) && country === form_constants_1.CountryEnum.Canada) {
        var date = dateString.split('-');
        month = parseInt(date[1], 10);
        day = parseInt(date[2], 10);
        year = parseInt(date[0], 10);
    }
    else if (ddMMyyyyFormat.test(dateString) && country === form_constants_1.CountryEnum.UnitedKingdom) {
        var date = dateString.split('/');
        month = parseInt(date[1], 10);
        day = parseInt(date[0], 10);
        year = parseInt(date[2], 10);
    }
    else {
        return false;
    }
    // Create list of days of a month [assume there is no leap year by default]
    var ListofDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    if (month === 2) {
        var leapYear = false;
        if ((!(year % 4) && year % 100) || !(year % 400)) {
            leapYear = true;
        }
        if ((leapYear === false) && (day >= 29)) {
            return false;
        }
        else if ((leapYear === true) && (day > 29)) {
            return false;
        }
    }
    else if (month >= 1 && month <= 12) {
        if (day > ListofDays[month - 1]) {
            return false;
        }
    }
    else {
        return false;
    }
    return true;
}
exports.isValidDate = isValidDate;
function isDateInRange(selectedDate, minDate, maxDate, earliestAllowed, latestAllowed) {
    if (!selectedDate)
        return false;
    var minDateValue = minDate && Date.parse(minDate);
    var maxDateValue = maxDate && Date.parse(maxDate);
    if (!!earliestAllowed && earliestAllowed.trim() !== '') {
        var earliestAllowedValue = (earliestAllowed && earliestAllowed.toLowerCase() == "today") ? moment().format("MM/DD/YYYY") : earliestAllowed;
        var earliestAllowedValueDate = Date.parse(earliestAllowedValue);
        minDateValue = (minDateValue && earliestAllowedValueDate < minDateValue) ? earliestAllowedValueDate : minDateValue;
    }
    if (!!latestAllowed && latestAllowed.trim() !== '') {
        var latestAllowedValue = (latestAllowed && latestAllowed.toLowerCase() == "today") ? moment().format("MM/DD/YYYY") : latestAllowed;
        var latestAllowedValueDate = Date.parse(latestAllowedValue);
        maxDateValue = (maxDateValue && latestAllowedValueDate > maxDateValue) ? latestAllowedValueDate : maxDateValue;
    }
    var selectedDateValue = Date.parse(selectedDate);
    if (isNaN(selectedDateValue)) {
        var dateArray = selectedDate.split('/');
        var usDateFormat = dateArray[1] + "/" + dateArray[0] + "/" + dateArray[2];
        selectedDateValue = Date.parse(usDateFormat);
    }
    if (isNaN(selectedDateValue) || (maxDateValue && selectedDateValue > maxDateValue) || (minDateValue && selectedDateValue < minDateValue))
        return false;
    return true;
}
exports.isDateInRange = isDateInRange;
/**
 * Given a UTC date string, apply the the current time zone, and format the result as a string.
 * @param {string} originalUtcDateTime A UTC datetime string, typically from an API or database.
 * This is an example: '2019-09-12T18:23:31'
 * @param {string} desiredDateFormat The format for the returned value. If this is blank/null/undefined,
 * the default is 'MM/DD/YYYY'
 * @returns {string} A localized version of the original utc date, formatted as a string.
 * If the original utc date is blank/null/undefined, an empty string is returned.
 */
function getLocalDateString(originalUtcDate, desiredDateFormat) {
    if (desiredDateFormat === void 0) { desiredDateFormat = null; }
    if (!originalUtcDate || originalUtcDate.trim() === "") {
        return "";
    }
    if (!desiredDateFormat || desiredDateFormat.trim() === "") {
        desiredDateFormat = "MM/DD/YYYY";
    }
    return moment.utc(originalUtcDate).local().format(desiredDateFormat);
}
exports.getLocalDateString = getLocalDateString;
//# sourceMappingURL=date-utilities.js.map


//////////////////
// WEBPACK FOOTER
// ./~/@social-solutions/ssg-forms-processing/dist/utilities/date-utilities.js
// module id = 1154
// module chunks = 0